import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { login, submitMfa, requestCaptcha } from '../api';
import { VALORANT_REGIONS, type ValorantRegion } from '@vst/shared';
import { Button, Disclaimer, PageHeader } from '../components/ui';
import { HCaptcha } from '../components/HCaptcha';
import { hapticNotif } from '../lib/telegram';

interface Props {
  defaultRegion: ValorantRegion;
}

type Phase = 'credentials' | 'captcha' | 'mfa';

export function LoginPage({ defaultRegion }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [phase, setPhase] = useState<Phase>('credentials');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [region, setRegion] = useState<ValorantRegion>(defaultRegion);

  const [captchaSessionId, setCaptchaSessionId] = useState('');
  const [captchaSiteKey, setCaptchaSiteKey] = useState('');
  const [captchaData, setCaptchaData] = useState('');

  const [mfaEmail, setMfaEmail] = useState('');
  const [mfaSession, setMfaSession] = useState('');
  const [mfaCode, setMfaCode] = useState('');

  const [error, setError] = useState<string | null>(null);

  // --- Step 1: submit credentials → request captcha challenge ---
  const captchaMut = useMutation({
    mutationFn: () => requestCaptcha(),
    onError: (e: Error) => {
      hapticNotif('error');
      setError(e.message);
    },
    onSuccess: (res) => {
      setError(null);
      setCaptchaSessionId(res.sessionId);
      setCaptchaSiteKey(res.captchaSiteKey);
      setCaptchaData(res.captchaData);
      setPhase('captcha');
    },
  });

  // --- Step 2: after captcha resolved → do full login ---
  const loginMut = useMutation({
    mutationFn: (captchaToken: string) =>
      login({
        sessionId: captchaSessionId,
        username,
        password,
        captchaToken,
        region,
      }),
    onError: (e: Error) => {
      hapticNotif('error');
      setError(e.message);
      setPhase('credentials'); // back to creds on failure
    },
    onSuccess: (res) => {
      setError(null);
      if ('requireMultifactor' in res && res.requireMultifactor) {
        setMfaEmail(res.multifactorEmail);
        setMfaSession(res.sessionId);
        setPhase('mfa');
        return;
      }
      hapticNotif('success');
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });

  // --- Step 3: MFA ---
  const mfaMut = useMutation({
    mutationFn: () => submitMfa({ sessionId: mfaSession, code: mfaCode }),
    onError: (e: Error) => {
      hapticNotif('error');
      setError(e.message);
    },
    onSuccess: () => {
      hapticNotif('success');
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });

  const handleCaptchaVerified = useCallback(
    (token: string) => {
      loginMut.mutate(token);
    },
    [loginMut],
  );

  return (
    <div className="pt-6">
      <PageHeader title={t('login.title')} subtitle={t('login.subtitle')} />
      <Disclaimer />

      <form
        className="mt-6 space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (phase === 'credentials') captchaMut.mutate();
          else if (phase === 'mfa') mfaMut.mutate();
        }}
      >
        {phase === 'credentials' && (
          <>
            <Field label={t('login.username')}>
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                className={inputCls}
                required
              />
            </Field>
            <Field label={t('login.password')}>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className={inputCls}
                required
              />
            </Field>
            <Field label={t('login.region')}>
              <select
                value={region}
                onChange={(e) => setRegion(e.target.value as ValorantRegion)}
                className={inputCls}
              >
                {VALORANT_REGIONS.map((r) => (
                  <option key={r} value={r}>
                    {r.toUpperCase()}
                  </option>
                ))}
              </select>
            </Field>
          </>
        )}

        {phase === 'captcha' && (
          <div className="space-y-3">
            <div className="text-sm text-valo-muted text-center">
              Пройди проверку, мы не робот 🙂
            </div>
            <HCaptcha
              siteKey={captchaSiteKey}
              rqData={captchaData}
              onVerified={handleCaptchaVerified}
              onError={() => setError('hCaptcha error')}
            />
            {loginMut.isPending && (
              <div className="text-xs text-valo-muted text-center animate-pulse">
                Входим в Riot...
              </div>
            )}
          </div>
        )}

        {phase === 'mfa' && (
          <>
            <div className="text-sm text-valo-muted">
              {t('login.mfaHint', { email: mfaEmail })}
            </div>
            <Field label={t('login.mfaCode')}>
              <input
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                inputMode="numeric"
                autoComplete="one-time-code"
                className={`${inputCls} font-mono text-center text-lg tracking-[0.5em]`}
                required
              />
            </Field>
          </>
        )}

        {error && (
          <div className="clip-corners-sm border border-valo-red/40 bg-valo-red/10 px-3 py-2 text-sm text-valo-red">
            {error}
          </div>
        )}

        {phase !== 'captcha' && (
          <Button
            type="submit"
            className="w-full"
            disabled={captchaMut.isPending || loginMut.isPending || mfaMut.isPending}
          >
            {captchaMut.isPending || mfaMut.isPending
              ? t('common.loading')
              : phase === 'credentials'
                ? t('login.signIn')
                : t('common.confirm')}
          </Button>
        )}
      </form>
    </div>
  );
}

const inputCls =
  'w-full clip-corners-sm bg-valo-navy-soft border border-valo-navy-border px-3 py-2.5 text-valo-white ' +
  'focus:outline-none focus:border-valo-red transition-colors';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-widest text-valo-muted mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}
